<script setup lang="ts">
import type {
  EditModeFieldProps,
  FieldEmits,
  FieldProps,
  GroundedFieldEmits,
} from '@/modules/Project/Fields/field-types'
import type { Field } from '@/modules/Project/Fields/types'
import { getFieldClaims } from '@/modules/Project/Fields/utils/grounding'
import { useFieldStatusIndicator } from '@/modules/Project/useFieldStatusIndicator'
import { useFieldValue } from '@/modules/Project/useFieldValue'
import { formatJson } from '@/shared/utils/formatting'
import HighlightedTextarea from '@/uiKit/HighlightedTextarea.vue'
import { twMerge } from 'tailwind-merge'
import { computed, ref, useTemplateRef, watch } from 'vue'

const props = defineProps<FieldProps<'json'> & EditModeFieldProps>()

const emit = defineEmits<FieldEmits<'json'> & GroundedFieldEmits>()

const computedField = computed(() => props.field)
const value = useFieldValue(computedField)
const { isModelOutput } = useFieldStatusIndicator(computedField)
const textareaRef = useTemplateRef('textareaRef')

const claims = computed(() => getFieldClaims(props.field))

const localValue = ref<string | null>(value.value)

watch(
  value,
  (newValue) => {
    const nextValue = isModelOutput.value ? newValue : formatJson(newValue)
    localValue.value = nextValue
  },
  { immediate: true },
)

function onChange(e: Field<'json'>['manualValue']) {
  localValue.value = e
  emit('change', e)
  emit('submit', e)
}
defineExpose({ focus: () => textareaRef.value?.focus() })
</script>

<template>
  <HighlightedTextarea
    ref="textareaRef"
    variant="transparent"
    :data-test="`json-sidebar-${props.property.id}`"
    lang="json"
    :value="localValue || ''"
    :claims="isModelOutput ? claims : undefined"
    size="md"
    :class="
      twMerge(
        'h-auto max-h-[640px] grow font-mono hover:bg-background-transparent-hovered',
        String($attrs.class || ''),
      )
    "
    :readonly="readonly"
    @input="localValue = $event"
    @show-source="$emit('show-source', $event)"
    @change="onChange"
    @keydown.escape="localValue = value"
  />
</template>
